import React, { useState } from 'react';
import { Car } from 'lucide-react';

// Hooks
import { useLang } from './hooks/useLang.js';
import { useCookieConsent } from './hooks/useCookieConsent.js';
import { useListings } from './hooks/useListings.js';
import { useArbitrageFilters } from './hooks/useArbitrageFilters.js';
import { useOpportunities } from './hooks/useOpportunities.js';
import { useFavourites } from './hooks/useFavourites.js';
import { useFeedback } from './hooks/useFeedback.js';
import { usePagination } from './hooks/usePagination.js';
import { useAuth } from './hooks/useAuth.js';

// Components
import { Header } from './components/Header.jsx';
import { WelcomeBanner } from './components/WelcomeBanner.jsx';
import { FavouritesIndicator } from './components/FavouritesIndicator.jsx';
import { FilterPanel } from './components/FilterPanel.jsx';
import { StatsBar } from './components/StatsBar.jsx';
import { SortControls } from './components/SortControls.jsx';
import { OpportunityCard } from './components/OpportunityCard.jsx';
import { LoadMoreSection } from './components/LoadMoreSection.jsx';
import { EmptyState } from './components/EmptyState.jsx';
import { Footer } from './components/Footer.jsx';
import { LegalModal } from './components/LegalModal.jsx';
import { CookieConsentBar } from './components/CookieConsentBar.jsx';
import { BackToTopButton } from './components/BackToTopButton.jsx';
import { LoginScreen } from './components/LoginScreen.jsx';

export default function CarArbitrageDemo() {
  // --- Hooks ---
  const { lang, changeLang, t } = useLang();
  const { isAuthenticated, isValidating, error: authError, user, login, logout } = useAuth();
  const { cookieConsent, handleCookieChoice, legalModal, setLegalModal } = useCookieConsent();
  const filters = useArbitrageFilters();
  const { favourites, showFavourites, setShowFavourites, toggleFavourite } = useFavourites();
  const feedback = useFeedback();

  const {
    allListings, loading,
    availableCountries, availableMakes, availableModels, availableVersions, availableFuelTypes,
    lastUpdate,
  } = useListings();

  const {
    displayedOpps, filteredListingsCount, totalProfitPotential, computingOpps,
  } = useOpportunities(allListings, filters, favourites, showFavourites);

  const { visibleCount, isLoadingMore, showBackToTop, loadMore } = usePagination(
    displayedOpps.length,
    filters.filterKey
  );

  // --- Local UI state ---
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showFilters, setShowFilters] = useState(true);

  // --- Derived ---
  const visibleOpps = displayedOpps.slice(0, visibleCount);
  const hasMore = visibleCount < displayedOpps.length;

  // --- Auth gate ---
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center">
        <div className="text-center">
          <Car className="w-16 h-16 mx-auto mb-4 text-emerald-400 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen t={t} onLogin={login} isValidating={isValidating} error={authError} />;
  }

  // --- Loading screen ---
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center">
        <div className="text-center">
          <Car className="w-16 h-16 mx-auto mb-4 text-emerald-400 animate-pulse" />
          <p className="text-slate-400">{t('loadingOpportunities')}</p>
        </div>
      </div>
    );
  }

  // --- Main render ---
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <Header
        t={t}
        lang={lang}
        changeLang={changeLang}
        showFavourites={showFavourites}
        setShowFavourites={setShowFavourites}
        favourites={favourites}
        totalProfitPotential={totalProfitPotential}
        onLogout={logout}
        user={user}
      />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <WelcomeBanner
          t={t}
          lang={lang}
          showWelcome={showWelcome}
          setShowWelcome={setShowWelcome}
          generalEmail={feedback.generalEmail}
          setGeneralEmail={feedback.setGeneralEmail}
          generalMessage={feedback.generalMessage}
          setGeneralMessage={feedback.setGeneralMessage}
          generalSending={feedback.generalSending}
          generalSent={feedback.generalSent}
          sendGeneralFeedback={feedback.sendGeneralFeedback}
          lastUpdate={lastUpdate}
        />

        <FavouritesIndicator
          t={t}
          showFavourites={showFavourites}
          setShowFavourites={setShowFavourites}
          displayedOppsLength={displayedOpps.length}
        />

        <FilterPanel
          t={t}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          sourceCountry={filters.sourceCountry}
          setSourceCountry={filters.setSourceCountry}
          targetCountry={filters.targetCountry}
          setTargetCountry={filters.setTargetCountry}
          filterMake={filters.filterMake}
          setFilterMake={filters.setFilterMake}
          filterModel={filters.filterModel}
          setFilterModel={filters.setFilterModel}
          filterVersion={filters.filterVersion}
          setFilterVersion={filters.setFilterVersion}
          filterFuelType={filters.filterFuelType}
          setFilterFuelType={filters.setFilterFuelType}
          maxPrice={filters.maxPrice}
          setMaxPrice={filters.setMaxPrice}
          minMileage={filters.minMileage}
          setMinMileage={filters.setMinMileage}
          maxMileage={filters.maxMileage}
          setMaxMileage={filters.setMaxMileage}
          minYear={filters.minYear}
          setMinYear={filters.setMinYear}
          maxYear={filters.maxYear}
          setMaxYear={filters.setMaxYear}
          availableCountries={availableCountries}
          availableMakes={availableMakes}
          availableModels={availableModels}
          availableVersions={availableVersions}
          availableFuelTypes={availableFuelTypes}
          allListings={allListings}
          qualityLevels={filters.qualityLevels}
          setQualityLevels={filters.setQualityLevels}
          yearTolerance={filters.yearTolerance}
          setYearTolerance={filters.setYearTolerance}
          mileageTolerance={filters.mileageTolerance}
          setMileageTolerance={filters.setMileageTolerance}
        />

        <StatsBar
          t={t}
          displayedOpps={displayedOpps}
          filteredListingsCount={filteredListingsCount}
        />

        <SortControls
          t={t}
          sortBy={filters.sortBy}
          setSortBy={filters.setSortBy}
          sortDir={filters.sortDir}
          setSortDir={filters.setSortDir}
        />

        <div className="grid gap-3 sm:gap-4">
          {visibleOpps.map((opp) => (
            <OpportunityCard
              key={opp.id}
              t={t}
              lang={lang}
              opp={opp}
              isSelected={selectedOpp?.id === opp.id}
              onSelect={(o) => setSelectedOpp(selectedOpp?.id === o.id ? null : o)}
              isFavourite={favourites.includes(opp.id)}
              onToggleFavourite={toggleFavourite}
              targetCountry={filters.targetCountry}
              reportOppId={feedback.reportOppId}
              setReportOppId={feedback.setReportOppId}
              reportEmail={feedback.reportEmail}
              setReportEmail={feedback.setReportEmail}
              reportMessage={feedback.reportMessage}
              setReportMessage={feedback.setReportMessage}
              reportSending={feedback.reportSending}
              reportSent={feedback.reportSent}
              sendReport={feedback.sendReport}
            />
          ))}
        </div>

        {computingOpps ? (
          <div className="text-center py-12">
            <Car className="w-12 h-12 mx-auto mb-3 text-emerald-400 animate-pulse" />
            <p className="text-slate-400">{t('loadingOpportunities')}</p>
          </div>
        ) : displayedOpps.length === 0 ? (
          <EmptyState t={t} showFavourites={showFavourites} />
        ) : (
          <LoadMoreSection
            t={t}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            loadMore={loadMore}
            visibleCount={visibleOpps.length}
            displayedOppsLength={displayedOpps.length}
          />
        )}
      </div>

      <Footer t={t} setLegalModal={setLegalModal} />
      <LegalModal t={t} legalModal={legalModal} setLegalModal={setLegalModal} />
      {cookieConsent === null && <CookieConsentBar t={t} handleCookieChoice={handleCookieChoice} />}
      {showBackToTop && <BackToTopButton t={t} />}
    </div>
  );
}
